import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTabs } from '../context/TabsContext';
import { useAuth } from '../context/authContext';
import { getSubscriptionStatus } from '../services/subscriptionService';

const FloatingPantryTab = () => {
    // Collapse functionality removed - tabs always visible
    const navigate = useNavigate();
    const location = useLocation();
    const { user, userProfile } = useAuth();
    const [isPremiumUser, setIsPremiumUser] = useState(false);
    const [checkingPremium, setCheckingPremium] = useState(true);
    
    // Drag and position state
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState(() => {
        // Load saved position from localStorage
        try {
            const saved = localStorage.getItem('pantryTabPosition');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed && typeof parsed === 'object' && 
                    (parsed.edge === 'right' || parsed.edge === 'bottom') &&
                    typeof parsed.offset === 'number' && 
                    parsed.offset >= 0) {
                    
                    if (typeof window !== 'undefined') {
                        const viewportWidth = window.innerWidth;
                        const viewportHeight = window.innerHeight;
                        const buttonSize = 88;
                        
                        if (parsed.edge === 'right') {
                            const maxOffset = Math.max(24, viewportHeight - buttonSize - 24);
                            return {
                                edge: parsed.edge,
                                offset: Math.max(24, Math.min(parsed.offset, maxOffset))
                            };
                        } else {
                            const maxOffset = Math.max(24, viewportWidth - buttonSize - 24);
                            return {
                                edge: parsed.edge,
                                offset: Math.max(24, Math.min(parsed.offset, maxOffset))
                            };
                        }
                    }
                    return parsed;
                }
            }
        } catch (e) {
            console.warn('Failed to parse pantryTabPosition from localStorage:', e);
        }
        // Default position: right edge, below other tabs
        return { 
            edge: 'right', 
            offset: typeof window !== 'undefined' 
                ? Math.max(24, Math.round(window.innerHeight * 0.4) + 100) 
                : 24 
        };
    });
    
    // Check premium status - also check userProfile for admin/family/beta status
    useEffect(() => {
        const checkPremium = async () => {
            if (!user) {
                setIsPremiumUser(false);
                setCheckingPremium(false);
                return;
            }
            
            // First check if user is admin/family/beta directly from userProfile (most reliable)
            if (userProfile && (userProfile.is_admin || userProfile.is_family || userProfile.is_beta)) {
                setIsPremiumUser(true);
                setCheckingPremium(false);
                return;
            }
            
            try {
                const subscription = await getSubscriptionStatus();
                const isPremium = subscription && ['unlimited', 'beta', 'admin', 'family'].includes(subscription.planType);
                setIsPremiumUser(isPremium);
            } catch (err) {
                console.error('Error checking subscription status:', err);
                setIsPremiumUser(false);
            } finally {
                setCheckingPremium(false);
            }
        };
        
        checkPremium();
    }, [user, userProfile]);

    // Use refs to avoid stale closures
    const positionRef = useRef(position);
    const dragStartRef = useRef(null);
    const buttonRef = useRef(null);
    const isDraggingRef = useRef(false);
    const locationRef = useRef(location);
    const navigateRef = useRef(navigate);
    
    // Keep refs in sync with state
    useEffect(() => {
        positionRef.current = position;
    }, [position]);
    
    useEffect(() => {
        isDraggingRef.current = isDragging;
    }, [isDragging]);
    
    useEffect(() => {
        locationRef.current = location;
    }, [location]);
    
    useEffect(() => {
        navigateRef.current = navigate;
    }, [navigate]);

    // Save position to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('pantryTabPosition', JSON.stringify(position));
    }, [position]);

    // Validate and clamp position when window resizes
    useEffect(() => {
        const validatePosition = () => {
            if (typeof window === 'undefined') return;
            
            setPosition(currentPosition => {
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const buttonSize = 88;
                
                let needsUpdate = false;
                let newPosition = { ...currentPosition };
                
                if (currentPosition.edge === 'right') {
                    const maxOffset = Math.max(24, viewportHeight - buttonSize - 24);
                    if (currentPosition.offset > maxOffset) {
                        newPosition.offset = maxOffset;
                        needsUpdate = true;
                    }
                } else {
                    const maxOffset = Math.max(24, viewportWidth - buttonSize - 24);
                    if (currentPosition.offset > maxOffset) {
                        newPosition.offset = maxOffset;
                        needsUpdate = true;
                    }
                }
                
                return needsUpdate ? newPosition : currentPosition;
            });
        };
        
        validatePosition();
        window.addEventListener('resize', validatePosition);
        
        return () => {
            window.removeEventListener('resize', validatePosition);
        };
    }, []);

    const getDynamicStyle = () => {
        const bodyPadding = 'var(--spacing-sm)';
        const buttonSize = 88; // Always full size
        
        let safeOffset = position.offset;
        if (typeof window !== 'undefined') {
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            
            if (position.edge === 'right') {
                const maxOffset = Math.max(24, viewportHeight - 88 - 24);
                safeOffset = Math.max(24, Math.min(position.offset, maxOffset));
            } else {
                const maxOffset = Math.max(24, viewportWidth - 88 - 24);
                safeOffset = Math.max(24, Math.min(position.offset, maxOffset));
            }
        }
        
        if (position.edge === 'right') {
            return {
                right: `calc(0px - ${bodyPadding})`,
                top: `${safeOffset}px`,
                position: 'fixed',
                margin: '0',
                transform: 'translateY(0)',
                zIndex: '9999',
                cursor: isDragging ? 'grabbing' : 'grab'
            };
        } else {
            return {
                bottom: `calc(0px - ${bodyPadding})`,
                left: `${safeOffset}px`,
                position: 'fixed',
                margin: '0',
                transform: 'translateY(0)',
                zIndex: '9999',
                cursor: isDragging ? 'grabbing' : 'grab'
            };
        }
    };

    const handleMouseDown = (e) => {
        if (e.button !== 0) return;
        
        setIsDragging(false);
        isDraggingRef.current = false;
        dragStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            startPos: { ...positionRef.current },
            hasMoved: false
        };
        e.preventDefault();
    };

    const handleTouchStart = (e) => {
        const touch = e.touches[0];
        
        setIsDragging(false);
        isDraggingRef.current = false;
        dragStartRef.current = {
            x: touch.clientX,
            y: touch.clientY,
            startPos: { ...positionRef.current },
            hasMoved: false
        };
        e.preventDefault();
    };

    const handleMouseMove = (e) => {
        if (!dragStartRef.current) return;
        
        const deltaX = e.clientX - dragStartRef.current.x;
        const deltaY = e.clientY - dragStartRef.current.y;
        const moveThreshold = 10;
        // Collapse functionality removed
        
        if (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold) {
            if (dragStartRef.current) {
                dragStartRef.current.hasMoved = true;
                
                if (!isDraggingRef.current) {
                    setIsDragging(true);
                    isDraggingRef.current = true;
                }
                
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const buttonSize = 88;
                const margin = 24;
                
                let newPosition = { ...startPos };
                newPosition.edge = 'right';
                newPosition.offset = Math.max(margin, Math.min(viewportHeight - buttonSize - margin, e.clientY - buttonSize / 2));
                
                setPosition(newPosition);
            }
        }
    };

    const handleTouchMove = (e) => {
        if (!dragStartRef.current) return;
        
        const touch = e.touches[0];
        const deltaX = touch.clientX - dragStartRef.current.x;
        const deltaY = touch.clientY - dragStartRef.current.y;
        const moveThreshold = 10;
        // Collapse functionality removed
        
        if (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold) {
            if (dragStartRef.current) {
                dragStartRef.current.hasMoved = true;
                
                if (!isDraggingRef.current) {
                    setIsDragging(true);
                    isDraggingRef.current = true;
                }
                
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const buttonSize = 88;
                const margin = 24;
                
                let newPosition = { ...startPos };
                newPosition.edge = 'right';
                newPosition.offset = Math.max(margin, Math.min(viewportHeight - buttonSize - margin, touch.clientY - buttonSize / 2));
                
                setPosition(newPosition);
            }
        }
    };

    const handleMouseUp = (e) => {
        let shouldClick = false;
        if (dragStartRef.current) {
            const hasMoved = dragStartRef.current.hasMoved || false;
            const deltaX = e.clientX - dragStartRef.current.x;
            const deltaY = e.clientY - dragStartRef.current.y;
            const moveThreshold = 10;
            
            shouldClick = !hasMoved && Math.abs(deltaX) <= moveThreshold && Math.abs(deltaY) <= moveThreshold;
        }
        
        setIsDragging(false);
        isDraggingRef.current = false;
        dragStartRef.current = null;
        
        if (shouldClick) {
            // Collapse functionality removed - always handle clicks
            
            // Check if user is admin - only allow navigation if admin
            const userProfileData = userProfile;
            const isAdmin = userProfileData?.is_admin;
            
            // Use ref to get current location
            const currentPath = locationRef.current?.pathname || location.pathname;
            const currentIsPantryPage = currentPath === '/grocery/stock' || currentPath === '/kitchen-stock';
            
            if (!isAdmin && !currentIsPantryPage) {
                // Don't navigate to pantry page if not admin
                return;
            }
            
            const navigateFn = navigateRef.current || navigate;
            
            if (currentIsPantryPage) {
                navigateFn('/grocery/generate');
            } else if (isAdmin) {
                navigateFn('/grocery/stock');
            }
        }
    };

    const handleTouchEnd = (e) => {
        let shouldClick = false;
        if (dragStartRef.current && e.changedTouches[0]) {
            const hasMoved = dragStartRef.current.hasMoved || false;
            const touch = e.changedTouches[0];
            const deltaX = touch.clientX - dragStartRef.current.x;
            const deltaY = touch.clientY - dragStartRef.current.y;
            const moveThreshold = 10;
            
            shouldClick = !hasMoved && Math.abs(deltaX) <= moveThreshold && Math.abs(deltaY) <= moveThreshold;
        }
        
        setIsDragging(false);
        isDraggingRef.current = false;
        dragStartRef.current = null;
        
        if (shouldClick) {
            // Collapse functionality removed - always handle clicks
            
            // Check if user is admin - only allow navigation if admin
            const userProfileData = userProfile;
            const isAdmin = userProfileData?.is_admin;
            
            // Use ref to get current location
            const currentPath = locationRef.current?.pathname || location.pathname;
            const currentIsPantryPage = currentPath === '/grocery/stock' || currentPath === '/kitchen-stock';
            
            if (!isAdmin && !currentIsPantryPage) {
                // Don't navigate to pantry page if not admin
                return;
            }
            
            const navigateFn = navigateRef.current || navigate;
            
            if (currentIsPantryPage) {
                navigateFn('/grocery/generate');
            } else if (isAdmin) {
                navigateFn('/grocery/stock');
            }
        }
    };

    // Set up event listeners
    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('touchmove', handleTouchMove);
        document.addEventListener('touchend', handleTouchEnd);
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, []);

    // Determine page type - use location directly (it's reactive and will cause re-renders)
    const currentPath = location.pathname;
    const isGroceryListPage = currentPath === '/grocery/generate' || currentPath.startsWith('/grocery/');
    const isPantryPage = currentPath === '/grocery/stock' || currentPath === '/kitchen-stock';
    const isAdmin = userProfile?.is_admin;
    const shouldShow = (isGroceryListPage || isPantryPage) && !checkingPremium && isPremiumUser;
    
    // Don't show if:
    // - Still checking premium status
    // - Not a premium user
    // - Not on grocery list page or pantry page
    if (!shouldShow) {
        return null;
    }

    const showComingSoon = !isAdmin && !isPantryPage;

    return (
        <div
            ref={buttonRef}
            className="fixed z-[9999] group"
            style={getDynamicStyle()}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
        >
            <div className={`bg-neutral-0 hairline transition-all duration-200 ${
                position.edge === 'right' 
                    ? `rounded-l-lg border-l border-t border-b w-20 h-16` 
                    : `rounded-t-lg border-t border-l border-r w-16 h-20`
            } ${showComingSoon ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            style={{
                margin: '0 !important',
                padding: '0 !important',
                backgroundColor: '#FFFFFF',
                borderTop: `1px solid #0D0D0D`,
                borderRight: position.edge === 'right' ? 'none' : '1px solid #0D0D0D',
                borderLeft: position.edge === 'right' ? '1px solid #0D0D0D' : '1px solid #0D0D0D',
                borderBottom: position.edge === 'right' ? '1px solid #0D0D0D' : 'none',
                boxShadow: position.edge === 'right' ? '-2px 0 2px rgba(0, 0, 0, 0.02)' : '0 -1px 2px rgba(0, 0, 0, 0.02)',
                position: 'relative',
                display: 'block',
                cursor: isDragging ? 'grabbing' : (showComingSoon ? 'not-allowed' : 'grab'),
                opacity: showComingSoon ? 0.5 : 1
            }}>
                <div className="w-full h-full flex items-center justify-center overflow-hidden p-1">
                    <div className="w-full h-full flex items-center justify-center bg-neutral-50">
                            {isPantryPage ? (
                                // X icon when on pantry page (to go back to grocery list)
                                <svg 
                                    width="24" 
                                    height="24" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="#1b263b" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    className="w-8 h-8"
                                >
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            ) : (
                                // Pantry icon when on grocery list page
                                <svg 
                                    width="24" 
                                    height="24" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="#1b263b" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    className="w-8 h-8"
                                >
                                    <rect x="3" y="3" width="18" height="18" rx="2" />
                                    <line x1="3" y1="9" x2="21" y2="9" />
                                    <line x1="3" y1="15" x2="21" y2="15" />
                                </svg>
                            )}
                        </div>
                </div>
                {/* Coming Soon Tooltip */}
                {showComingSoon && (
                    <div className={`absolute ${
                        position.edge === 'right' 
                            ? 'right-full top-1/2 -translate-y-1/2 mr-2' 
                            : 'bottom-full left-1/2 -translate-x-1/2 mb-2'
                    } w-48 bg-neutral-900 text-neutral-0 p-3 border border-neutral-200 shadow-lg z-50 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200`}>
                        <div className="font-mono text-xs uppercase tracking-wider mb-1">
                            COMING SOON
                        </div>
                        <div className="text-sm">
                            Kitchen Stock is currently available for admin users only.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FloatingPantryTab;

